import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Button,
  IconButton,
  TextField,
  Divider,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Save,
  Cancel,
  Person,
  Security,
  Notifications,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import profileService, { type UserProfile, type UpdateProfileRequest } from '../services/profileService';
import UserRoleSections from '../components/UserRoleSections';
import { childManagementService, type ChildSearchResult } from '../services/childManagementService';
import ChildEditPanel from '../components/ChildEditPanel';
import { authService } from '../services/authService';

const TIMEZONE_OPTIONS = [
  'UTC',
  'Europe/London',
  'Europe/Paris',
  'Asia/Kolkata',
  'Asia/Dubai',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
];

const LOCALE_OPTIONS = [
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-IN', label: 'English (India)' },
];

const ParentProfile: React.FC = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [children, setChildren] = useState<ChildSearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedProfile, setEditedProfile] = useState<UpdateProfileRequest | null>(null);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);
  const [childPasswordDialog, setChildPasswordDialog] = useState<{ open: boolean; childId: number | null; childName: string }>({ open: false, childId: null, childName: '' });
  const [editingChildId, setEditingChildId] = useState<number | null>(null);
  const [childNewPassword, setChildNewPassword] = useState('');
  const [childPasswordError, setChildPasswordError] = useState<string | null>(null);
  const [childPasswordSaving, setChildPasswordSaving] = useState(false);
  const [setPasswordDialog, setSetPasswordDialog] = useState(false);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [setPasswordForm, setSetPasswordForm] = useState({ code: '', new: '', confirm: '' });
  const [setPasswordCodeSent, setSetPasswordCodeSent] = useState(false);
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const profileData = await profileService.getProfile();
      setProfile(profileData);

      const currentUser = authService.getCurrentUser();
      if (currentUser?.id) {
        const linkedChildren = await childManagementService.getLinkedChildren(currentUser.id);
        setChildren(linkedChildren);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load profile';
      setLoadError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleEditToggle = () => {
    if (editing) {
      setEditing(false);
      setEditedProfile(null);
    } else if (profile) {
      setEditing(true);
      setEditedProfile({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phoneNumber: profile.phoneNumber || '',
        timezone: profile.timezone || '',
        locale: profile.locale || '',
      });
    }
  };

  const handleSave = async () => {
    if (!editedProfile) return;
    setSaving(true);
    try {
      const updated = await profileService.updateProfile(editedProfile);
      setProfile(updated);
      setEditing(false);
      setEditedProfile(null);

      const stored = authService.getCurrentUser();
      if (stored) {
        localStorage.setItem(
          'user',
          JSON.stringify({
            ...stored,
            firstName: updated.firstName,
            lastName: updated.lastName,
          })
        );
      }
      setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to save profile';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof UpdateProfileRequest, value: string) => {
    if (!editedProfile) return;
    setEditedProfile({ ...editedProfile, [field]: value });
  };

  const handleRequestSetPasswordCode = async () => {
    if (!profile?.email || !profile.userType) return;
    setPasswordError(null);
    setPasswordSaving(true);
    try {
      await authService.requestLoginCode(profile.email, profile.userType);
      setSetPasswordCodeSent(true);
      setSnackbar({ open: true, message: 'Sign-in code sent to your email', severity: 'success' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send code';
      setPasswordError(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleSetPasswordWithCode = async () => {
    setPasswordError(null);
    if (setPasswordForm.new !== setPasswordForm.confirm) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      await profileService.setPasswordWithLoginCode(
        setPasswordForm.code,
        setPasswordForm.new,
        setPasswordForm.confirm
      );
      setSetPasswordDialog(false);
      setSetPasswordForm({ code: '', new: '', confirm: '' });
      setSetPasswordCodeSent(false);
      await loadData();
      setSnackbar({ open: true, message: 'Password set successfully', severity: 'success' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to set password';
      setPasswordError(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleChangeChildPassword = async () => {
    if (!childPasswordDialog.childId || !childNewPassword.trim()) return;
    setChildPasswordError(null);
    setChildPasswordSaving(true);
    try {
      await childManagementService.changeChildPassword(childPasswordDialog.childId, childNewPassword);
      setChildPasswordDialog({ open: false, childId: null, childName: '' });
      setChildNewPassword('');
      setSnackbar({ open: true, message: 'Child password updated successfully', severity: 'success' });
    } catch (err: unknown) {
      setChildPasswordError(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setChildPasswordSaving(false);
    }
  };

  const handleChangePassword = async () => {
    setPasswordError(null);
    if (passwords.new !== passwords.confirm) {
      setPasswordError('New passwords do not match.');
      return;
    }
    setPasswordSaving(true);
    try {
      await profileService.changePassword({
        currentPassword: passwords.current,
        newPassword: passwords.new,
        confirmPassword: passwords.confirm,
      });
      setChangePasswordDialog(false);
      setPasswords({ current: '', new: '', confirm: '' });
      setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to change password';
      setPasswordError(msg);
    } finally {
      setPasswordSaving(false);
    }
  };

  const displayFirstName = editing ? editedProfile?.firstName : profile?.firstName;
  const displayLastName = editing ? editedProfile?.lastName : profile?.lastName;
  const displayPhone = editing ? editedProfile?.phoneNumber : profile?.phoneNumber;
  const displayTimezone = editing ? editedProfile?.timezone : profile?.timezone;
  const displayLocale = editing ? editedProfile?.locale : profile?.locale;

  const avatarLabel = profile
    ? `${profile.firstName?.charAt(0) || ''}${profile.lastName?.charAt(0) || ''}`.toUpperCase()
    : '?';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (loadError || !profile) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {loadError || 'Failed to load profile information.'}
        </Alert>
        <Button variant="contained" onClick={loadData}>
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              navigate('/parent-dashboard');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Parent Dashboard
          </Link>
          <Typography color="text.primary">Profile</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/parent-dashboard')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
              Profile Settings
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Manage your account information and preferences
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {editing ? (
            <>
              <Button variant="outlined" onClick={handleEditToggle} startIcon={<Cancel />}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleEditToggle} startIcon={<Edit />}>
              Edit Profile
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
                <Avatar sx={{ width: 100, height: 100, fontSize: 36, bgcolor: 'primary.main' }}>
                  {profile.profilePictureUrl ? (
                    <img src={profile.profilePictureUrl} alt="" style={{ width: '100%', height: '100%' }} />
                  ) : (
                    avatarLabel
                  )}
                </Avatar>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {displayFirstName} {displayLastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {profile.email}
                  </Typography>
                  {profile.createdAt && (
                    <Typography variant="body2" color="text.secondary">
                      Member since {new Date(profile.createdAt).toLocaleDateString()}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="First Name"
                    value={displayFirstName || ''}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    disabled={!editing}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Last Name"
                    value={displayLastName || ''}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    disabled={!editing}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Email Address" type="email" value={profile.email} disabled fullWidth />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Phone Number"
                    value={displayPhone || ''}
                    onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                    disabled={!editing}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!editing}>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      label="Timezone"
                      value={displayTimezone || ''}
                      onChange={(e) => handleInputChange('timezone', e.target.value)}
                    >
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <MenuItem key={tz} value={tz}>
                          {tz}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth disabled={!editing}>
                    <InputLabel>Language &amp; region</InputLabel>
                    <Select
                      label="Language & region"
                      value={displayLocale || ''}
                      onChange={(e) => handleInputChange('locale', e.target.value)}
                    >
                      {LOCALE_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                My Children
              </Typography>
              {children.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No linked children yet. Link a child from the parent dashboard.
                </Typography>
              ) : (
                <List sx={{ p: 0 }}>
                  {children.map((child, index) => (
                    <React.Fragment key={child.id}>
                      <ListItem sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: 'secondary.main' }}>
                              {child.firstName?.charAt(0)}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={`${child.firstName} ${child.lastName}`}
                            secondary={`${child.gradeLevel || '—'} • ${child.schoolName || '—'}`}
                          />
                        </Box>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setEditingChildId(child.id)}
                          sx={{ mt: 1, ml: 7 }}
                        >
                          Edit Profile
                        </Button>
                      </ListItem>
                      {index < children.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Account Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {profile.birthYear && (new Date().getFullYear() - profile.birthYear) < 16 ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Your parent manages your account credentials — ask them to change it from the Parent Dashboard.
                  </Alert>
                ) : profile.hasPassword ? (
                  <Button
                    variant="outlined"
                    startIcon={<Security />}
                    onClick={() => setChangePasswordDialog(true)}
                    fullWidth
                  >
                    Change Password
                  </Button>
                ) : profile.googleLinked ? (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    This account uses Google sign-in. Password change is not available.
                  </Alert>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<Security />}
                    onClick={() => {
                      setPasswordError(null);
                      setSetPasswordCodeSent(false);
                      setSetPasswordForm({ code: '', new: '', confirm: '' });
                      setSetPasswordDialog(true);
                    }}
                    fullWidth
                  >
                    Set Password with Email Code
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<Notifications />}
                  onClick={() => navigate('/parent-settings')}
                  fullWidth
                >
                  Notification Settings
                </Button>
                <Button variant="outlined" startIcon={<Person />} disabled fullWidth>
                  Language &amp; Region (edit above)
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Role-specific profile sections */}
      <UserRoleSections />

      <Dialog
        open={setPasswordDialog}
        onClose={() => !passwordSaving && setSetPasswordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Set Password</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
            Request a fresh 6-digit sign-in code to your email, then enter it below with your new password.
          </Typography>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <Button
            variant="outlined"
            onClick={handleRequestSetPasswordCode}
            disabled={passwordSaving}
            fullWidth
            sx={{ mb: 2 }}
          >
            {setPasswordCodeSent ? 'Resend sign-in code' : 'Send sign-in code to my email'}
          </Button>
          <TextField
            label="Sign-in code"
            value={setPasswordForm.code}
            onChange={(e) => setSetPasswordForm((p) => ({ ...p, code: e.target.value.replace(/\D/g, '').slice(0, 6) }))}
            fullWidth
            sx={{ mb: 2 }}
            inputProps={{ maxLength: 6, inputMode: 'numeric' }}
          />
          <TextField
            label="New Password"
            type="password"
            value={setPasswordForm.new}
            onChange={(e) => setSetPasswordForm((p) => ({ ...p, new: e.target.value }))}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={setPasswordForm.confirm}
            onChange={(e) => setSetPasswordForm((p) => ({ ...p, confirm: e.target.value }))}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetPasswordDialog(false)} disabled={passwordSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSetPasswordWithCode} disabled={passwordSaving}>
            {passwordSaving ? 'Saving...' : 'Save Password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={changePasswordDialog}
        onClose={() => !passwordSaving && setChangePasswordDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            {(['current', 'new', 'confirm'] as const).map((field) => (
              <TextField
                key={field}
                label={
                  field === 'current'
                    ? 'Current Password'
                    : field === 'new'
                      ? 'New Password'
                      : 'Confirm New Password'
                }
                type={showPasswords[field] ? 'text' : 'password'}
                value={passwords[field]}
                onChange={(e) => setPasswords((prev) => ({ ...prev, [field]: e.target.value }))}
                fullWidth
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }))}
                      edge="end"
                    >
                      {showPasswords[field] ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChangePasswordDialog(false)} disabled={passwordSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleChangePassword} disabled={passwordSaving}>
            {passwordSaving ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Child Profile Dialog */}
      <Dialog
        open={editingChildId !== null}
        onClose={() => setEditingChildId(null)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          Edit Child Profile
        </DialogTitle>
        <DialogContent dividers>
          {editingChildId !== null && (
            <ChildEditPanel
              childId={editingChildId}
              onClose={() => setEditingChildId(null)}
              onSaved={() => {
                // Refresh the children list to pick up any name/school changes
                if (profile?.id) {
                  childManagementService.getLinkedChildren(profile.id)
                    .then(setChildren)
                    .catch(() => {});
                }
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingChildId(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Change Child Password Dialog */}
      <Dialog
        open={childPasswordDialog.open}
        onClose={() => !childPasswordSaving && setChildPasswordDialog({ open: false, childId: null, childName: '' })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Change Password for {childPasswordDialog.childName}</DialogTitle>
        <DialogContent>
          {childPasswordError && <Alert severity="error" sx={{ mb: 2 }}>{childPasswordError}</Alert>}
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={childNewPassword}
            onChange={(e) => setChildNewPassword(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChildPasswordDialog({ open: false, childId: null, childName: '' })} disabled={childPasswordSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleChangeChildPassword} disabled={childPasswordSaving || !childNewPassword.trim()}>
            {childPasswordSaving ? 'Saving...' : 'Set Password'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
      />
    </Container>
  );
};

export default ParentProfile;
